import { useEffect, useRef } from "react";
import { useScreenSize } from "../hooks/useScreenSize";
import BibleBottomBar from "./BibleBottomBar";
import { colors } from "./constants";

function Verse({
  verse,
  fontSize = 20,
  highlightedText,
  isUnderlined,
  onFocusFromClick,
  verseRef,
}: {
  verse: any;
  fontSize: number;
  highlightedText?: string;
  isUnderlined: boolean;
  onFocusFromClick: (verseNumber: number) => void;
  verseRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      id={`verse-${verse.verse_number}`}
      ref={verseRef}
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        outline: 'none',
        minWidth: 0,
      }}
    >
      <a
        href={`#verse-${verse.verse_number}`}
        onClick={(e) => {
          e.preventDefault();
          onFocusFromClick(verse.verse_number);
        }}
        style={{
          color: colors['verseIndexColor'],
          minWidth: `${fontSize}px`,
          fontSize: `${fontSize * 0.75}px`,
          userSelect: 'none',
          textDecoration: 'none',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      >
        {verse.verse_number}
      </a>
      <div
        style={{
          fontFamily: 'var(--bible-font-family)',
          fontSize: `${fontSize}px`,
          color: colors['black'],
          minWidth: 0,
          overflowWrap: 'break-word',
          textDecoration: isUnderlined ? 'underline' : 'none',
          textDecorationColor: isUnderlined ? colors['verseUnderline'] : undefined,
          textUnderlineOffset: isUnderlined ? '3px' : undefined,
        }}
        dangerouslySetInnerHTML={{ __html: highlightedText ?? verse.text }}
      />
    </div>
  )
}

export default function BibleVerses({
  verses,
  chosenTranslation,
  chosenBook,
  chosenChapter,
  highlightMap = {},
  focusedVerse = null,
  onFocusVerse,
  columns = 1,
  fontSize = 20,
  direction = 'ltr',
}: {
  verses: any[];
  chosenTranslation: any;
  chosenBook: any;
  chosenChapter: any;
  highlightMap?: Record<number, string>;
  focusedVerse?: number | null;
  onFocusVerse: (verseNumber: number) => void;
  columns?: number;
  fontSize?: number;
  direction?: 'ltr' | 'rtl';
}) {
  const verseRefs = useRef(new Map<number, HTMLDivElement>());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrolledVerse = useRef<number | null>(null);

  const scrollToVerseNumber = (verseNumber: number) => {
    lastScrolledVerse.current = verseNumber;
    verseRefs.current.get(verseNumber)?.scrollIntoView({ block: 'center' });
  };

  const focusVerse = (verseNumber: number) => {
    onFocusVerse(verseNumber);
    lastScrolledVerse.current = verseNumber;
  };

  useEffect(() => {
    if (focusedVerse != null && focusedVerse !== lastScrolledVerse.current) {
      scrollToVerseNumber(focusedVerse);
    }
  }, [focusedVerse]);

  const handleFocusFromClick = (verseNumber: number) => {
    focusVerse(verseNumber);
  };

  const versesPerColumn = Math.ceil(verses.length / columns);
  const columnArrays = [];

  for (let i = 0; i < columns; i++) {
    const startIndex = i * versesPerColumn;
    const endIndex = Math.min(startIndex + versesPerColumn, verses.length);
    columnArrays.push(verses.slice(startIndex, endIndex));
  }

  const { screenLargerThan } = useScreenSize()

  return (
    <div
      ref={scrollContainerRef}
      style={{
        display: 'flex',
        overflowY: 'auto',
        overflowX: 'hidden',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <div style={{
        // On mobile the bottom bar scrolls with the content instead of sitting
        // at the bottom of the viewport, so the verses must size to their own
        // content - flex:1 here would stretch them to fill the scroll container
        // and push the bar back down to the bottom edge.
        flex: screenLargerThan('lg') ? 1 : '0 0 auto',
        padding: '4px',
        display: 'flex',
        flexDirection: 'row',
        gap: '16px',
        direction: direction,
        minWidth: 0,
        boxSizing: 'border-box',
      }}>
        {columnArrays.map((columnVerses, columnIndex) => (
          <div key={columnIndex} style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            minWidth: 0,
          }}>
            {columnVerses.map((verse: any) => (
              <Verse
                key={`verse-${chosenBook.slug}-${verse.verse_number}`}
                verse={verse}
                fontSize={fontSize}
                highlightedText={highlightMap[verse.verse_number]}
                isUnderlined={focusedVerse === verse.verse_number}
                onFocusFromClick={handleFocusFromClick}
                verseRef={(el) => {
                  if (el) {
                    verseRefs.current.set(verse.verse_number, el);
                  } else {
                    verseRefs.current.delete(verse.verse_number);
                  }
                }}
              />
            ))}
          </div>
        ))}
      </div>
      {/* On mobile, chapter navigation follows the verses in the reading scroller. */}
      {!screenLargerThan('lg') && (
        <BibleBottomBar
          currentTranslation={chosenTranslation}
          currentBook={chosenBook}
          currentChapter={chosenChapter}
        />
      )}
    </div>
  )
}
